import React, { useState, useEffect } from 'react';
import { Mail, Lock, Eye, EyeOff, User } from 'lucide-react';

/**
 * CadastroSeguro - Componente de UI pura para tela de cadastro
 * 
 * Props:
 * - onSubmitRegister: (nome, email, senha, confirmarSenha) => void
 * - loading: boolean
 * - errorMessage?: string | null
 * - logoSrc?: string
 */
const CadastroSeguro = ({
  onSubmitRegister,
  loading = false,
  errorMessage = null,
  logoSrc = '/logo.png'
}) => {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [particles, setParticles] = useState([]);
  const [fadeIn, setFadeIn] = useState(false);

  // Generate particles on mount
  useEffect(() => {
    setFadeIn(true);
    
    const generateParticles = () => {
      const newParticles = [];
      for (let i = 0; i < 300; i++) {
        newParticles.push({
          id: i,
          left: Math.random() * 100,
          size: Math.random() * 4 + 2,
          duration: Math.random() * 10 + 15,
          delay: Math.random() * 5
        });
      }
      setParticles(newParticles);
    };
    
    generateParticles();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!loading) {
      onSubmitRegister(nome, email, senha, confirmarSenha);
    }
  };

  return (
    <>
      <style>{`
        /* Reutiliza os mesmos estilos do LoginSeguro */
        .cadastro-container-seguro {
          min-height: 100vh;
          background: linear-gradient(135deg, #0a0a0a 0%, #1c1c1c 50%, #0a0a0a 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          position: relative;
          overflow: hidden;
        }

        .grid-background-cadastro {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-image: 
            linear-gradient(rgba(255, 255, 255, 0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.03) 1px, transparent 1px);
          background-size: 50px 50px;
          z-index: 0;
        }

        .red-glow-top-left-cadastro {
          position: absolute;
          top: -200px;
          left: -200px;
          width: 800px;
          height: 800px;
          background: radial-gradient(
            circle,
            rgba(207, 13, 29, 0.3) 0%,
            rgba(207, 13, 29, 0.15) 30%,
            transparent 70%
          );
          animation: redGlow1 8s ease-in-out infinite alternate;
          z-index: 1;
        }

        .red-glow-bottom-right-cadastro {
          position: absolute;
          bottom: -150px;
          right: -150px;
          width: 600px;
          height: 600px;
          background: radial-gradient(
            circle,
            rgba(207, 13, 29, 0.25) 0%,
            rgba(207, 13, 29, 0.1) 30%,
            transparent 70%
          );
          animation: redGlow2 10s ease-in-out infinite alternate;
          z-index: 1;
        }

        @keyframes redGlow1 {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 0.6;
          }
          50% {
            opacity: 0.8;
          }
          100% {
            transform: translate(50px, 50px) scale(1.1);
            opacity: 0.6;
          }
        }

        @keyframes redGlow2 {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 0.5;
          }
          50% {
            opacity: 0.7;
          }
          100% {
            transform: translate(-40px, -40px) scale(1.15);
            opacity: 0.5;
          }
        }

        .particle-cadastro {
          position: absolute;
          background: #CF0D1D;
          border-radius: 50%;
          box-shadow: 0 0 10px rgba(207, 13, 29, 0.5);
          opacity: 0.6;
          animation: rise linear infinite;
          z-index: 2;
        }

        @keyframes rise {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 0.6;
          }
          90% {
            opacity: 0.6;
          }
          100% {
            transform: translateY(-100vh) rotate(360deg);
            opacity: 0;
          }
        }

        .cadastro-card-seguro {
          max-width: 500px;
          width: 100%;
          padding: 3rem 2.5rem;
          border-radius: 24px;
          background: rgba(28, 28, 28, 0.85);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.5),
            0 0 0 1px rgba(207, 13, 29, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.05);
          position: relative;
          z-index: 10;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.8s cubic-bezier(0.4, 0, 0.2, 1),
                      transform 0.8s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .cadastro-card-seguro.fade-in {
          opacity: 1;
          transform: translateY(0);
        }

        .logo-container-cadastro {
          display: flex;
          justify-content: center;
          margin-bottom: 2rem;
        }

        .logo-cadastro {
          width: 200px;
          height: auto;
          filter: drop-shadow(0 4px 12px rgba(207, 13, 29, 0.4));
        }

        .title-cadastro {
          text-align: center;
          margin-bottom: 2rem;
        }

        .title-cadastro h1 {
          font-size: 1.75rem;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 0.5rem;
        }

        .title-cadastro p {
          font-size: 0.875rem;
          color: rgba(255, 255, 255, 0.6);
        }

        .input-wrapper-cadastro {
          position: relative;
          margin-bottom: 1rem;
        }

        .input-icon-cadastro {
          position: absolute;
          left: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255, 255, 255, 0.4);
          transition: color 0.3s ease;
          z-index: 1;
        }

        .input-cadastro {
          width: 100%;
          height: 48px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 0 3rem 0 3rem;
          color: #ffffff !important;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          -webkit-text-fill-color: #ffffff;
        }

        .input-cadastro::placeholder {
          color: rgba(255, 255, 255, 0.3);
        }

        .input-cadastro:focus {
          outline: none;
          background: rgba(255, 255, 255, 0.08);
          border-color: #CF0D1D;
          box-shadow: 
            0 0 0 3px rgba(207, 13, 29, 0.1),
            0 0 20px rgba(207, 13, 29, 0.2);
        }

        .input-cadastro:focus + .input-icon-cadastro {
          color: #CF0D1D;
        }

        .toggle-password-cadastro {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: rgba(255, 255, 255, 0.4);
          cursor: pointer;
          transition: color 0.3s ease;
          z-index: 1;
        }

        .toggle-password-cadastro:hover {
          color: #CF0D1D;
        }

        .cadastro-button-seguro {
          width: 100%;
          height: 50px;
          background: #CF0D1D;
          color: #ffffff;
          border: none;
          border-radius: 12px;
          font-size: 1rem;
          font-weight: 600;
          letter-spacing: 0.5px;
          box-shadow: 0 4px 16px rgba(207, 13, 29, 0.3);
          margin-top: 0.5rem;
          cursor: pointer;
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .cadastro-button-seguro::before {
          content: '';
          position: absolute;
          width: 100%;
          height: 100%;
          left: -100%;
          top: 0;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
          transition: left 0.5s ease;
        }

        .cadastro-button-seguro:hover {
          background: #e60f21;
          transform: translateY(-2px);
          box-shadow: 0 6px 24px rgba(207, 13, 29, 0.4);
        }

        .cadastro-button-seguro:hover::before {
          left: 100%;
        }

        .cadastro-button-seguro:active {
          transform: translateY(0);
          box-shadow: 0 2px 8px rgba(207, 13, 29, 0.3);
        }

        .cadastro-button-seguro:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .divider-cadastro {
          border-top: 1px solid rgba(255, 255, 255, 0.1);
          padding-top: 1rem;
          margin-top: 1.5rem;
        }

        .login-text-cadastro {
          display: flex;
          justify-content: center;
          gap: 0.5rem;
          font-size: 0.875rem;
        }

        .login-text-cadastro span {
          color: rgba(255, 255, 255, 0.6);
        }

        .login-link-cadastro {
          color: #CF0D1D;
          font-weight: 600;
          text-decoration: none;
          position: relative;
          transition: color 0.3s ease;
        }

        .login-link-cadastro::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          width: 0;
          height: 2px;
          background: #CF0D1D;
          transition: width 0.3s ease;
        }

        .login-link-cadastro:hover::after {
          width: 100%;
        }

        .error-message-cadastro {
          background: rgba(239, 68, 68, 0.1);
          border: 1px solid rgba(239, 68, 68, 0.3);
          color: #ef4444;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          font-size: 0.875rem;
          margin-bottom: 1rem;
        }

        @media (max-width: 640px) {
          .cadastro-card-seguro {
            padding: 2rem 1.5rem;
            border-radius: 20px;
          }

          .logo-cadastro {
            width: 180px;
          }

          .input-cadastro,
          .cadastro-button-seguro {
            height: 46px;
          }

          .title-cadastro h1 {
            font-size: 1.5rem;
          }
        }
      `}</style>

      <div className="cadastro-container-seguro">
        {/* Camada 0: Grid */}
        <div className="grid-background-cadastro"></div>

        {/* Camada 1: Luzes vermelhas */}
        <div className="red-glow-top-left-cadastro"></div>
        <div className="red-glow-bottom-right-cadastro"></div>

        {/* Camada 2: Partículas */}
        {particles.map((particle) => (
          <div
            key={particle.id}
            className="particle-cadastro"
            style={{
              left: `${particle.left}%`,
              bottom: '-10px',
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              animationDuration: `${particle.duration}s`,
              animationDelay: `${particle.delay}s`
            }}
          />
        ))}

        {/* Camada 10: Card de Cadastro */}
        <div className={`cadastro-card-seguro ${fadeIn ? 'fade-in' : ''}`}>
          {/* Logo */}
          <div className="logo-container-cadastro">
            <img src={logoSrc} alt="Logo" className="logo-cadastro" />
          </div>

          {/* Título */}
          <div className="title-cadastro">
            <h1>Criar Conta</h1>
            <p>Preencha seus dados para se cadastrar</p>
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="error-message-cadastro">
              {errorMessage}
            </div>
          )}

          {/* Formulário */}
          <form onSubmit={handleSubmit}>
            {/* Nome */}
            <div className="input-wrapper-cadastro">
              <input
                type="text"
                placeholder="Nome completo"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className="input-cadastro"
                required
                disabled={loading}
              />
              <User className="input-icon-cadastro" size={20} />
            </div>

            {/* Email */}
            <div className="input-wrapper-cadastro">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input-cadastro"
                required
                disabled={loading}
              />
              <Mail className="input-icon-cadastro" size={20} />
            </div>

            {/* Senha */}
            <div className="input-wrapper-cadastro">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Senha"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                className="input-cadastro"
                required
                disabled={loading}
                minLength={6}
              />
              <Lock className="input-icon-cadastro" size={20} />
              <div
                className="toggle-password-cadastro"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </div>
            </div>

            {/* Confirmar Senha */}
            <div className="input-wrapper-cadastro">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirmar senha"
                value={confirmarSenha}
                onChange={(e) => setConfirmarSenha(e.target.value)}
                className="input-cadastro"
                required
                disabled={loading}
                minLength={6}
              />
              <Lock className="input-icon-cadastro" size={20} />
              <div
                className="toggle-password-cadastro"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </div>
            </div>

            {/* Botão Cadastrar */}
            <button
              type="submit"
              className="cadastro-button-seguro"
              disabled={loading}
            >
              {loading ? 'CADASTRANDO...' : 'CADASTRAR'}
            </button>
          </form>

          {/* Footer */}
          <div className="divider-cadastro">
            <div className="login-text-cadastro">
              <span>Já tem uma conta?</span>
              <a href="/login" className="login-link-cadastro">
                Faça login
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CadastroSeguro;
